import * as THREE from 'three';
import {PerspectiveCamera} from '../camera';

export class InputMotion {
    boost: boolean;
    forward: boolean;
    backward: boolean;
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;

    constructor() {
        this.boost = false;
        this.forward = false;
        this.backward = false;
        this.left = false;
        this.right = false;
        this.up = false;
        this.down = false;
    }
}

interface Angles {
    theta: number;
    phi: number;
}

interface Listeners {
    enabled: (() => void)[],
    disabled: (() => void)[],
}

interface MouseMoveEvent {
    movementX: number,
    movementY: number,
}

interface Moving {
    start: {
        position: THREE.Vector3,
        target: THREE.Vector3,
    },
    end: {
        position: THREE.Vector3,
        target: THREE.Vector3,
    },
    duration: number,
    elapsed: number,
}

export class PointerLock {

    camera: PerspectiveCamera;
    element: HTMLElement | Document;

    forward: THREE.Vector3;
    left: THREE.Vector3;
    up: THREE.Vector3;

    ignored: boolean;
    enabled: boolean;

    speed: number;
    sensitivity: number;

    inputMotion: InputMotion;
    angles: Angles;
    mouseMove: THREE.Vector2;

    listeners: Listeners;
    moving?: Moving;

    constructor(camera: PerspectiveCamera, speed: number, sensitivity: number, element: HTMLElement | Document = document) {
        this.camera = camera;
        this.element = element;
        this.forward = camera.position.clone().multiplyScalar(-1);
        this.left = new THREE.Vector3();
        this.up = new THREE.Vector3();

        this.ignored = false;
        this.enabled = true;

        this.angles = {
            phi: 0,
            theta: 0
        };

        this.speed = speed;
        this.sensitivity = sensitivity;
        this.inputMotion = new InputMotion();
        this.mouseMove = new THREE.Vector2();

        this.listeners = {enabled: [], disabled: []};

        document.addEventListener('keydown', (e) => this.onKeyDown(e));
        document.addEventListener('keyup', (e) => this.onKeyUp(e));
        document.addEventListener('mousemove', (e) => this.onMouseMove(e as unknown as MouseMoveEvent));
        document.addEventListener('wheel', (e) => this.onMouseWheel(e as WheelEvent));
        element.addEventListener('click', () => document.body.requestPointerLock());

        document.addEventListener('pointerlockchange', () => {
            if (document.pointerLockElement != undefined) {
                for (let listener of this.listeners.enabled) {
                    listener();
                }
                this.enabled = true;
            } else {
                for (let listener of this.listeners.disabled) {
                    listener();
                }
                this.enabled = false;
            }
        });
    }

    addEventListener(type: 'enabled' | 'disabled', callback: () => void) {
        this.listeners[type].push(callback);
    }

    getTarget(): THREE.Vector3 {
        if (this.camera.target !== undefined) {
            return this.camera.target.clone();
        }
        return this.camera.position.clone().add(this.forward);
    }

    vectorFromAngles() {
        // Update direction
        this.forward.y = Math.sin(this.angles.phi);

        let cos = Math.cos(this.angles.phi);
        this.forward.z = cos * Math.cos(this.angles.theta);
        this.forward.x = cos * Math.sin(this.angles.theta);
        this.forward.normalize();

        this.left.copy(this.camera.up.clone().cross(this.forward)).normalize();
        this.up.copy(this.forward.clone().cross(this.left)).normalize();
    }

    anglesFromVectors() {
        let forward = this.getTarget().sub(this.camera.position).normalize();

        this.angles.phi = Math.asin(forward.y);

        // Don't know why this line works... But thanks Thierry-san and
        // Bastien because it seems to work...
        this.angles.theta = Math.atan2(forward.x, forward.z);
    }

    onKeyDown(event: KeyboardEvent) {
        this.onKeyEvent(event, true);
    }

    onKeyUp(event: KeyboardEvent) {
        this.onKeyEvent(event, false);
    }

    onKeyEvent(event: KeyboardEvent, toSet: boolean) {

        if (!this.enabled)
            return;

        switch (event.key) {
            // Azerty keyboards
            case "ArrowUp": case "z": case "w": this.inputMotion.forward = toSet; break;
            case "ArrowLeft": case "q": case "a": this.inputMotion.left = toSet; break;
            case "ArrowDown": case "s": this.inputMotion.backward = toSet; break;
            case "ArrowRight": case "d": this.inputMotion.right = toSet; break;
            case " ": this.inputMotion.boost = toSet; break;

            // Qwerty keyboards
            // case "ArrowUp": case "w": this.inputMotion.forward = toSet; break; // up / w
            // case "ArrowLeft": case "a": this.inputMotion.left = toSet; break; // left / a
            // case "ArrowDown": case "s": this.inputMotion.backward = toSet; break; // down / s
            // case "ArrowRight": case "d": this.inputMotion.right = toSet; break; // right / d

            // case 73: case 104: this.inputMotion.increasePhi   = toSet; break; // 8 Up for angle
            // case 75: case 98:  this.inputMotion.decreasePhi   = toSet; break; // 2 Down for angle
            // case 74: case 100: this.inputMotion.increaseTheta = toSet; break; // 4 Left for angle
            // case 76: case 102: this.inputMotion.decreaseTheta = toSet; break; // 6 Right for angle

            // Enter key
            // case 13: if (toSet) this.log(); break;
        }
    }

    onMouseMove(event: MouseMoveEvent) {
        if (!this.enabled)
            return;

        this.mouseMove.x = event.movementX * this.sensitivity;
        this.mouseMove.y = event.movementY * this.sensitivity;
    }

    onMouseWheel(event: WheelEvent) {
        if (!this.enabled)
            return;

        this.inputMotion.up = event.deltaY < 0.0;
        this.inputMotion.down = event.deltaY > 0.0;
    }

    update(time: number = 20): boolean {

        let moved = false;

        this.updateMovingIfNecessary(time);

        if (!this.enabled) {
            return false;
        }

        this.anglesFromVectors();

        // Update angles
        if (!this.ignored) {
            this.angles.theta -= this.mouseMove.x;
            this.angles.phi -= this.mouseMove.y;

            if (this.mouseMove.x !== 0 || this.mouseMove.y !== 0) {
                moved = true;
            }
        }

        const eps = 0.01;
        this.angles.phi = Math.max(-Math.PI / 2 + eps, Math.min(this.angles.phi, Math.PI / 2 - eps));
        this.mouseMove.set(0, 0);
        this.vectorFromAngles();

        // Update position
        let speed = this.speed * time / 20;
        let dir = new THREE.Vector3();

        let up = this.camera.up;
        let forward = this.forward.clone().normalize().multiplyScalar(100);
        let left = up.clone().cross(forward).normalize().multiplyScalar(100);

        if (!this.ignored) {
            if (this.inputMotion.boost) speed *= 10;
            if (this.inputMotion.forward) {moved = true; dir.add(forward.clone().multiplyScalar(speed));}
            if (this.inputMotion.backward) {moved = true; dir.sub(forward.clone().multiplyScalar(speed));}
            if (this.inputMotion.left) {moved = true; dir.add(left.clone().multiplyScalar(speed));}
            if (this.inputMotion.right) {moved = true; dir.sub(left.clone().multiplyScalar(speed));}
            if (this.inputMotion.up) {moved = true; dir.add(up.clone().multiplyScalar(speed * 300));}
            if (this.inputMotion.down) {moved = true; dir.sub(up.clone().multiplyScalar(speed * 300));}

            this.camera.position.add(dir);
        }

        this.inputMotion.up = this.inputMotion.down = false;

        this.camera.target = this.camera.position.clone().add(this.forward);
        this.camera.lookAt(this.camera.target);

        return moved;
    }

    moveTo(position: THREE.Vector3, target: THREE.Vector3, duration: number) {
        this.moving = {
            start: {
                position: this.camera.position.clone(),
                target: this.camera.target.clone().sub(this.camera.position).normalize().add(this.camera.position),
            },
            end: {
                position,
                target: target.clone().sub(position).normalize().add(position),
            },
            duration,
            elapsed: 0,
        };
        this.ignored = true;
    }

    updateMovingIfNecessary(time: number) {
        if (this.moving === undefined) {
            return;
        }

        this.moving.elapsed += time;
        let finished = false;
        let t = this.moving.elapsed / this.moving.duration;

        if (t > 1) {
            t = 1;
            finished = true;
            this.ignored = false;
        }

        this.camera.position.copy(
            this.moving.start.position.clone().multiplyScalar(1 - t).add(
                this.moving.end.position.clone().multiplyScalar(t)));

        this.camera.target.copy(
            this.moving.start.target.clone().multiplyScalar(1 - t).add(
                this.moving.end.target.clone().multiplyScalar(t)));

        if (finished) {
            this.moving = undefined;
        }

        this.camera.lookAt(this.camera.target);
    }
}
